import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { BillingProvider } from './contexts/BillingContext';
import { ThemeProvider } from './contexts/ThemeContext';
import App from './App';
import AuthErrorBoundary from './components/AuthErrorBoundary';
import './index.css';
import { PricingProvider } from './utils/PricingContext';
import { ToastProvider } from './components/ui/Toast';
import { initSecurity } from './utils/security';
import { queryClient as queryClientConfig } from './utils/optimizedApiInterceptors';
import * as Sentry from "@sentry/react";

// Initialize Sentry for production error tracking
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
          import.meta.env.VITE_API_URL || "localhost",
          /^\//,
        ],
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1, // Capture 10% of transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // Sample 10% of sessions
    replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors
    environment: import.meta.env.MODE,
    beforeSend(event, hint) {
      // Filter out non-critical errors if needed
      if (event.exception) {
        const error = hint.originalException;
        // Don't send AbortController errors (user navigated away)
        if (error && error.name === 'AbortError') {
          return null;
        }
      }
      return event;
    },
  });
}

// Initialize security measures (opt-in)
// Avoid unexpected reloads or storage changes that can interfere with auth.
if (import.meta.env.PROD && import.meta.env.VITE_SECURITY_ENABLE === 'true') {
  initSecurity();
}

// Create React Query client with optimized configuration
const queryClient = new QueryClient(queryClientConfig);

// Check if mock auth is enabled
const isMockAuthEnabled = import.meta.env.VITE_ENABLE_MOCK_AUTH === 'true';

// Security wrapper component
const SecureApp = () => {
  useEffect(() => {
    // Additional runtime security checks
    if (import.meta.env.PROD) {
      // Check if running in an iframe
      if (window.self !== window.top && window.top) {
        // If in iframe, redirect to a blank page or error page
        window.top.location.href = 'about:blank';
      }
    }
  }, []);

  return (
    <StrictMode>
      <Sentry.ErrorBoundary 
        fallback={({ error, resetError }) => (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <h1>Oops! Something went wrong</h1>
            <p>We&apos;ve been notified and are working on a fix.</p>
            <button onClick={resetError} style={{ 
              padding: '10px 20px', 
              marginTop: '10px',
              cursor: 'pointer',
              background: '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '4px'
            }}>
              Try again
            </button>
          </div>
        )}
        showDialog={false}
      >
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <AuthProvider>
              <OrganizationProvider>
                <BillingProvider>
                  <BrowserRouter>
                    <ToastProvider>
                      <PricingProvider>
                        <AuthErrorBoundary>
                          <App />
                        </AuthErrorBoundary>
                      </PricingProvider>
                    </ToastProvider>
                  </BrowserRouter>
                </BillingProvider>
              </OrganizationProvider>
            </AuthProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
    </StrictMode>
  );
};

createRoot(document.getElementById('root')!).render(<SecureApp />);
